import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

type NewStatus = "PAUSED" | "ACTIVE" | "INHERITED_FROM_SOURCE";

export interface CopySubmitPayload {
  newStatus: NewStatus;
  /** ISO-8601 UTC，若用户未填则为 null。 */
  startTime: string | null;
  endTime: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 当前复制目标数量（单条为 1，批量为 >=1）。仅用于文案展示。 */
  count: number;
  /** "Campaign" / "AdSet" / "Ad" */
  resourceLabel: string;
  /** Ad 层级传 false 隐藏排期字段（Meta 没有独立 Ad 排期）。 */
  supportsSchedule?: boolean;
  onSubmit: (payload: CopySubmitPayload) => void;
}

const STATUS_OPTIONS: { value: NewStatus; label: string; hint: string }[] = [
  { value: "PAUSED", label: "PAUSED（推荐）", hint: "新副本先暂停，确认后再启用" },
  { value: "ACTIVE", label: "ACTIVE", hint: "复制后立即投放" },
  { value: "INHERITED_FROM_SOURCE", label: "跟随源对象", hint: "保持原状态" },
];

function toIsoOrNull(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function BatchCopyDialog({
  open,
  onOpenChange,
  count,
  resourceLabel,
  supportsSchedule = true,
  onSubmit,
}: Props) {
  const [newStatus, setNewStatus] = useState<NewStatus>("PAUSED");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const submit = () => {
    onSubmit({
      newStatus,
      startTime: supportsSchedule ? toIsoOrNull(start) : null,
      endTime: supportsSchedule ? toIsoOrNull(end) : null,
    });
    onOpenChange(false);
  };

  const startIso = toIsoOrNull(start);
  const endIso = toIsoOrNull(end);
  const scheduleError =
    startIso && endIso && new Date(startIso).getTime() >= new Date(endIso).getTime()
      ? "结束时间必须晚于开始时间"
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {count === 1 ? `复制 ${resourceLabel}` : `批量复制 ${resourceLabel}`}
          </DialogTitle>
          <DialogDescription>
            {count === 1
              ? `Meta 会在同账号下创建新副本，子层级会一并复制。`
              : `将对选中的 ${count} 项执行复制。所有副本共享下方配置。`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">新副本状态</legend>
            {STATUS_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  checked={newStatus === opt.value}
                  onChange={() => setNewStatus(opt.value)}
                  className="mt-1"
                />
                <div>
                  <div>{opt.label}</div>
                  <div className="text-xs text-muted-foreground">{opt.hint}</div>
                </div>
              </label>
            ))}
          </fieldset>

          {supportsSchedule && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">排期（可选）</legend>
              <div className="text-xs text-muted-foreground">
                留空则继承源对象的起止时间。时间按本机时区输入，提交时自动转 UTC。
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-sm">
                  <span className="text-muted-foreground">开始时间</span>
                  <input
                    type="datetime-local"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </label>
                <label className="text-sm">
                  <span className="text-muted-foreground">
                    结束时间{resourceLabel === "Campaign" && "（对应 stop_time）"}
                  </span>
                  <input
                    type="datetime-local"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </label>
              </div>
              {scheduleError && (
                <div className="text-xs text-destructive">{scheduleError}</div>
              )}
            </fieldset>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
          <Button onClick={submit} disabled={!!scheduleError}>确定</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
